import React from 'react';
import { ApIcon, ApInput, colors } from '@alixpartners/ui-components';
import {
	StyledMenu,
	Wrapper,
	ListItem,
	ListTitle,
	List,
	ListInfo,
	InputWrapper,
} from './SearchInput.styles';
import isEqual from 'lodash/isEqual';

export class SearchInput extends React.Component {
	state = {
		query: '',
		isOpen: false,
		cursor: 0,
	};

	componentDidUpdate(prevProps, prevState) {
		const { selectedOption, isSelectable, placement } = this.props;
		const { isOpen, cursor, query } = this.state;

		if (selectedOption && !isEqual(selectedOption, prevProps.selectedOption)) {
			this.updateCursor();
		}

		if (isOpen && isOpen !== prevState.isOpen) {
			this.scrollToElement(cursor);
		}

		if (isSelectable && !isOpen && isOpen !== prevState.isOpen) {
			this.setState({ query: '' });
		}

		// Update position for placement top ApMenu
		// of dynamically changed height of matches container
		if (
			this.hasMatches() &&
			isOpen &&
			placement &&
			placement.includes('top') &&
			query !== prevState.query
		) {
			this.updateMatchesPosition();
		}
	}

	updateMatchesPosition = () => {
		this.setState({ isOpen: false }, () => this.toggleMenu(true));
	};

	updateCursor = () => {
		const { selectedOption } = this.props;
		const cursor = selectedOption
			? this.getMatches().findIndex(item => item.id === selectedOption.id)
			: 0;

		this.setState({ cursor });
	};

	onSearchChange = value => {
		const { isOpen } = this.state;
		const { onChange, isSelectable } = this.props;

		this.setState({ query: value, cursor: 0 });

		if (isSelectable) {
			onChange(null);
		}

		if (!isOpen) {
			this.toggleMenu(true);
		}
	};

	getMatches = () => {
		const { options, charCount, maxMatchesNumber } = this.props;
		const { query } = this.state;

		if (charCount && !query) {
			return [];
		}

		const matches = options.filter(({ name }) => {
			return name.toLowerCase().includes(query.toLowerCase());
		});

		return maxMatchesNumber ? matches.slice(0, maxMatchesNumber) : matches;
	};

	getHighlightedName = name => {
		const { query } = this.state;

		if (!query) {
			return name;
		}

		const index = name.toLowerCase().indexOf(query.toLowerCase());
		const before = name.slice(0, index);
		const searchValue = name.slice(index, index + query.length);
		const after = name.slice(index + query.length, name.length);

		return (
			<React.Fragment>
				{before}
				<span>{searchValue}</span>
				{after}
			</React.Fragment>
		);
	};

	handleListItemClick = option => {
		const { onChange } = this.props;

		this.setState({ isOpen: false, query: '' });
		onChange(option);
	};

	hasMatches = () => {
		const { charCount } = this.props;
		const { query } = this.state;

		return (
			this.getMatches().length > 0 && (!charCount || query.length >= charCount)
		);
	};

	handleKeyDown = e => {
		const { cursor, isOpen } = this.state;

		// Esc, close menu
		if (e.keyCode === 27) {
			this.setState({ isOpen: false });

			return;
		}

		if (!this.hasMatches()) {
			return;
		}

		// Arrow up
		if (e.keyCode === 38 && cursor > 0) {
			const newPosition = cursor - 1;

			this.setState({ cursor: newPosition });

			// Scroll to selected item if container has scroll
			this.scrollToElement(newPosition);
			// Arrow down
		} else if (e.keyCode === 40) {
			if (!isOpen) {
				this.toggleMenu(true);

				return;
			}

			if (cursor < this.getMatches().length - 1) {
				const newPosition = cursor + 1;

				this.setState({ cursor: newPosition });

				// Scroll to selected item if container has scroll
				this.scrollToElement(newPosition);
			}

			// Enter
		} else if (e.key === 'Enter') {
			if (isOpen) {
				this.handleListItemClick(this.getMatches()[cursor]);
			} else {
				this.toggleMenu(true);
			}
		}
	};

	toggleMenu = isOpen => {
		this.setState({ isOpen });
	};

	scrollToElement = index => {
		const el = document.getElementById(`item${index}`);

		// Scroll to selected item if container has scroll
		if (el) {
			el.scrollIntoView({
				block: 'end',
				inline: 'nearest',
			});
		}
	};

	clearSearch = () => {
		const { onChange } = this.props;

		this.setState({ query: '' });

		if (onChange) {
			onChange(null);
		}
	};

	render() {
		const { isOpen, cursor, query } = this.state;
		const {
			selectedOption,
			placeholder,
			showIcon,
			isDismissible,
			placement,
			options,
		} = this.props;

		return (
			<StyledMenu
				button={
					<div onKeyDown={e => this.handleKeyDown(e)}>
						<InputWrapper showIcon={showIcon}>
							{showIcon && (
								<ApIcon
									iconName="baseline_search"
									iconSize={18}
									iconColor={colors.grey6}
								/>
							)}
							<ApInput
								disabled={!options || options.length === 0}
								placeholder={placeholder}
								autoFocus
								onChange={this.onSearchChange}
								value={
									query || ((selectedOption && selectedOption.value) || '')
								}
							/>
							{isDismissible &&
								(query || selectedOption) && (
									<ApIcon
										iconName="baseline_close"
										iconSize={18}
										iconColor={colors.grey6}
										onClick={this.clearSearch}
									/>
								)}
						</InputWrapper>
					</div>
				}
				placement={placement || 'bottom-start'}
				isOpen={isOpen}
				toggleMenu={this.toggleMenu}
			>
				<Wrapper>
					{this.hasMatches() && (
						<List>
							{this.getMatches().map((option, index) => {
								const { id, name, info } = option;

								return (
									<ListItem
										key={`${name}-${id}`}
										className={`${cursor === index ? 'active' : ''}`}
										id={`item${index}`}
										onClick={() => {
											this.handleListItemClick(option);
										}}
									>
										<ListTitle>{this.getHighlightedName(name)}</ListTitle>
										<ListInfo>{info}</ListInfo>
									</ListItem>
								);
							})}
						</List>
					)}
				</Wrapper>
			</StyledMenu>
		);
	}
}

export default SearchInput;
